'use client'

import React from 'react'
import { Avatar as ChakraAvatar } from '@chakra-ui/react'
import { avatarColorPaletteFromName } from '../../utils/avatarColor.js'
import { resolveEntityLogoUrl } from '../../utils/staticUrl.js'

/**
 * Avatar.Root de Chakra v3 con colorPalette derivado del `name` del Fallback
 * (en v2 el fondo variaba; en v3 el default es gris).
 * Si ya hay `colorPalette` o `bg` explícito, no se sobrescribe.
 */
const AvatarRoot = React.forwardRef(function AvatarRoot(
  { colorPalette, children, ...props },
  ref,
) {
  let derivedName = ''
  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child)) return
    if (typeof child.props?.name === 'string' && child.props.name.trim()) {
      derivedName = child.props.name
    }
  })

  const hasExplicitBg =
    props.bg != null || props.background != null || props.bgColor != null
  const resolvedPalette =
    colorPalette ??
    (hasExplicitBg ? undefined : avatarColorPaletteFromName(derivedName))

  return (
    <ChakraAvatar.Root ref={ref} colorPalette={resolvedPalette} {...props}>
      {children}
    </ChakraAvatar.Root>
  )
})

/**
 * Logo/avatar de entidad: usa el recorte guardado en perfil (cover + fondo blanco).
 */
const AvatarImage = React.forwardRef(function AvatarImage(
  { src, objectFit, bg, ...props },
  ref,
) {
  const resolvedSrc = src ? resolveEntityLogoUrl(src) : src
  return (
    <ChakraAvatar.Image
      ref={ref}
      src={resolvedSrc}
      objectFit={objectFit ?? 'cover'}
      objectPosition="center"
      bg={bg ?? 'white'}
      {...props}
    />
  )
})

export const Avatar = {
  Root: AvatarRoot,
  Image: AvatarImage,
  Fallback: ChakraAvatar.Fallback,
  Icon: ChakraAvatar.Icon,
  Group: ChakraAvatar.Group,
  RootProvider: ChakraAvatar.RootProvider,
  PropsProvider: ChakraAvatar.PropsProvider,
  Context: ChakraAvatar.Context,
}
